/***
 *
 *   PAGINATE
 *   Pagination control to split results into multiple pages.
 *   Returns a new offset.
 *
 *   Ported from ui-reference/src/components/ui/table-pagination.tsx: the range
 *   sits on the left of a top-bordered footer row, the prev/next controls on
 *   the right as secondary buttons that disable at the ends.
 *
 *   PROPS
 *   total: the total number of results
 *   limit: the number of results per page
 *   offset: the current position
 *
 **********/

import { useState, useEffect } from 'react';
import { Button, ClassHelper } from 'components/lib';
import Style from './paginate.tailwind.js';
import { useTranslate } from 'app/translations.js';

export function Paginate(props) {
    const { _t } = useTranslate();
    const totalPages = Math.ceil(props.total / props.limit);
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        if (props.total) setTotal(props.total);
    }, [props.total]);

    const canPrev = page > 0;
    const canNext = page < totalPages - 1;

    function prev() {
        if (!canPrev) return;
        setPage(page - 1);
        props.onChange((page - 1) * props.limit);
    }

    function next() {
        if (!canNext) return;
        setPage(page + 1);
        props.onChange((page + 1) * props.limit);
    }

    const start = parseInt(props.offset + 1, 10); // add one so it doesn't start at 0
    const end = parseInt(props.offset, 10) + parseInt(props.limit, 10);

    const paginateStyle = ClassHelper(Style, {
        paginate: true,
        className: props.className,
    });

    return (
        <section className={paginateStyle}>
            <p className={Style.label}>
                {props.offset || props.limit
                    ? `${_t('table.showing') || 'Showing'} ${start} - ${end > total ? total : end} / ${total}`
                    : _t('result.none') || 'No results'}
            </p>

            <div className={Style.controls}>
                <div className={Style.buttons}>
                    <Button
                        icon="chevron-left"
                        variant="secondary"
                        size="sm"
                        className={Style.button}
                        disabled={!canPrev}
                        title={_t('previous')}
                        action={prev}
                    />
                    <Button
                        icon="chevron-right"
                        variant="secondary"
                        size="sm"
                        className={Style.button}
                        disabled={!canNext}
                        title={_t('next')}
                        action={next}
                    />
                </div>
            </div>
        </section>
    );
}
